<template>
  <div ref="messagesContainer" class="chat-messages">
    <div v-if="chatBotStore.messages.length === 0" class="empty-state">
      <div class="empty-icon">
        <img width="20" src="../img/KBuddyIcon.png" alt="KBuddy chat icon" />
      </div>
      <p>No messages yet. Start a conversation!</p>
    </div>

    <div v-for="message in chatBotStore.messages" :key="message.id" :class="['message', message.type]">
      <div class="message-avatar">
        <div v-if="message.type !== 'user'" class="user-avatar">
          <div class="bot-avatar"><img width="25" src="../img/KBuddyIcon.png" alt="KBuddy chat icon" /></div>
        </div>

        <div class="message-content">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="message-text" v-html="sanitizeHtml(message.text)"></div>
          <div v-if="message.type !== 'user'" class="feedback-wrapper">
            <ul class="rating-form">
              <li>
                <label>
                  <input
                    type="radio"
                    :name="`bot-response-usefulness-${message.id}`"
                    value="Response was useful"
                    @change="handleThumbsUp(message.id)"
                  />
                  <img width="25" src="../img/icon-like.png" alt="Like this response" />
                </label>
              </li>
              <li>
                <label>
                  <input
                    type="radio"
                    :name="`bot-response-usefulness-${message.id}`"
                    value="Response was not useful"
                    @change="setFeedbackRating(message.id, 'Response was not useful')"
                  />
                  <img width="25" src="../img/icon-dislike.png" alt="Dislike this response" />
                </label>
              </li>
            </ul>
            <div v-if="feedbackState[message.id]?.rating === 'Response was not useful'" class="submit-feedback">
              <textarea
                rows="3"
                placeholder="Share feedback (required)"
                class="feedback-textarea"
                :value="feedbackState[message.id]?.text || ''"
                @input="setFeedbackText(message.id, ($event.target as HTMLTextAreaElement).value)"
              />
              <div class="feedback-info">
                Your conversation will be included with your feedback to help improve our analysis.
              </div>
              <div class="feedback-button opposite-ends">
                <div class="message-time">{{ formatTime(message.timestamp) }}</div>
                <button id="submitFeedback" class="submit" @click="submitFeedback(message.id)">Submit feedback</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useChatBotStore } from "../stores/chatBotStore";
import { formatTime, scrollToBottom } from "../helpers/common";
import { onMounted, ref, reactive, watch } from "vue";
import DOMPurify from "dompurify";
import { FeedbackEntry } from "@/chatBot/interfaces/chatBotInterfaces";

const chatBotStore = useChatBotStore();

const messagesContainer = ref<HTMLElement>();

const feedbackState = reactive<Record<string, FeedbackEntry>>({});

const handleThumbsUp = (messageId: string) => {
  // Thumbs up doesn't open feedback section, just log/submit directly
  const payload = {
    messageId,
    rating: "Response was useful",
    feedbackText: "",
  };

  // TODO: Replace with actual API call when available
  console.log("Thumbs up submitted:", payload);
};

const setFeedbackRating = (messageId: string, rating: string) => {
  if (!feedbackState[messageId]) {
    feedbackState[messageId] = { rating: "", text: "" };
  }
  feedbackState[messageId].rating = rating;
};

const setFeedbackText = (messageId: string, text: string) => {
  if (!feedbackState[messageId]) {
    feedbackState[messageId] = { rating: "", text: "" };
  }
  feedbackState[messageId].text = text;
};

const submitFeedback = async (messageId: string) => {
  const feedback = feedbackState[messageId];
  if (!feedback?.rating) return;

  const payload = {
    messageId,
    rating: feedback.rating,
    feedbackText: feedback.text,
  };

  // TODO: Replace with actual API call when available
  console.log("Submitting feedback:", payload);

  // Mock API call
  try {
    // await api.submitChatFeedback(payload);
    console.log("Feedback submitted successfully");
  } catch (error) {
    console.error("Failed to submit feedback:", error);
  }
};

//Use package to sanitize HTML content to prevent XSS attacks
const sanitizeHtml = (html: string) => DOMPurify.sanitize(html);

onMounted(() => {
  scrollToBottom(messagesContainer.value!);
});

watch(
  () => chatBotStore.messages,
  () => {
    scrollToBottom(messagesContainer.value!);
  },
  { deep: true }
);
</script>
